<template>
    <div class="project-list">
      <div class="header">
        <h3>Projects</h3>
        <button @click="navigateToCreateProject" class="new-project-button">
          New Project
        </button>
      </div>
  
      <div class="search-filter-row">
        <input type="text" v-model="searchQuery" placeholder="Search by Name..." class="search-input" />
        <button @click="showFilters = !showFilters" class="toggle-filter-button">
          {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
        </button>
      </div>
  
      <!-- Collapsible Filter Menu -->
      <div v-if="showFilters" class="filters">
        <input type="date" v-model="startDate" class="date-input" placeholder="Start Date" />
        <input type="date" v-model="endDate" class="date-input" placeholder="End Date" />
      </div>
  
      <table class="project-table">
        <thead>
          <tr>
            <th @click="sortProjects('name')">Name<i v-if="currentSortColumn === 'name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortProjects('project_number')">Project Number<i v-if="currentSortColumn === 'project_number'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortProjects('status')">Status<i v-if="currentSortColumn === 'status'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortProjects('start_date')">Start Date<i v-if="currentSortColumn === 'start_date'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
            <th @click="sortProjects('end_date')">End Date<i v-if="currentSortColumn === 'end_date'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="project in paginatedProjects" :key="project.id" @click="navigateToProjectDetail(project.id)" class="project-row">
            <td>{{ project.name }}</td>
            <td>{{ project.project_number }}</td>
            <td>{{ project.status || '-' }}</td>
            <td>{{ formatDate(project.start_date) }}</td>
            <td>{{ formatDate(project.end_date) }}</td>
          </tr>
        </tbody>
      </table>
  
      <div class="pagination">
        <button v-for="page in visiblePageNumbers" :key="page" @click="setPage(page)" :class="{ active: page === currentPage }">{{ page }}</button>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { API_ENDPOINTS } from '@/services/config.js';
  import { mapState } from 'vuex';
  
  export default {
    data() {
      return {
        projects: [],
        searchQuery: '',
        currentPage: 1,
        projectsPerPage: 15,
        currentSortColumn: 'name',
        currentSortDirection: 'asc',
        showFilters: false,
        startDate: '',
        endDate: '',
        sortedProjects: [],
      };
    },
    created() {
      this.fetchProjects();
    },
    methods: {
      navigateToCreateProject() {
        this.$router.push({ path: '/create-project' });
      },
      async fetchProjects() {
        try {
          const response = await axios.get(API_ENDPOINTS.projects, {
          params: { entra_id: this.loggedInUser.entra_id }
        });
          this.projects = response.data;
          this.sortedProjects = [...this.projects];
          this.sortProjects(this.currentSortColumn);
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      },
      sortProjects(column) {
        if (this.currentSortColumn === column) {
          this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this.currentSortDirection = 'asc';
          this.currentSortColumn = column;
        }
        this.sortedProjects.sort((a, b) => {
          if (a[column] < b[column]) return this.currentSortDirection === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return this.currentSortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      },
      setPage(pageNumber) {
        this.currentPage = pageNumber;
      },
      setArrowDirection(sortDirection) {
        return sortDirection === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
      },
      navigateToProjectDetail(projectId) {
        this.$router.push({ name: 'ProjectDetailPage', params: { id: projectId } });
      },
      formatDate(dateString) {
        if (!dateString) {
        return "N/A";
        }
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
      },
    },
    computed: {
      ...mapState(['loggedInUser']), 
      filteredProjects() {
        return this.sortedProjects.filter(project => {
          const name = project.name?.toLowerCase() || '';
          const matchesSearchQuery = name.includes(this.searchQuery.toLowerCase());
          const matchesStartDate = this.startDate === '' || new Date(project.start_date) >= new Date(this.startDate);
          const matchesEndDate = this.endDate === '' || new Date(project.end_date) <= new Date(this.endDate);
          return matchesSearchQuery && matchesStartDate && matchesEndDate;
        });
      },
      paginatedProjects() {
        const start = (this.currentPage - 1) * this.projectsPerPage;
        const end = start + this.projectsPerPage;
        return this.filteredProjects.slice(start, end);
      },
      totalPages() {
        return Math.ceil(this.filteredProjects.length / this.projectsPerPage);
      },
      visiblePageNumbers() {
        const range = 15;
        let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
        let endPage = startPage + range - 1;
        if (endPage > this.totalPages) {
          endPage = this.totalPages;
          startPage = Math.max(1, endPage - range + 1);
        }
        return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
      },
      
    },
  };
  </script>
  
  <style scoped lang="scss">
  @import "@/assets/variables.scss";
  
  .project-list {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
  
      h3 {
        color: $primary-color;
      }
  
      .new-project-button {
        padding: 8px 16px;
        background-color: $primary-color;
        color: #fff;
        border: none;
        border-radius: 5px;
        cursor: pointer;
      }
  
      .new-project-button:hover {
        background-color: darken($primary-color, 10%);
      }
    }
  
    .search-filter-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
    }

    .search-input {
      flex: 1;
      margin: 0;
    }

    .toggle-filter-button {
      width: auto;
      margin: 0;
      padding: 6px 10px;
      white-space: nowrap;
    }

    .project-table {
      width: 100%;
    }
  
    .project-table th {
      background-color: $form-color;
      border: 1px solid $primary-color;
      cursor: pointer;
    }
  
    .filters {
      display: flex;
      justify-content: space-between;
      width: 100%;
      max-width: none;
      margin: 6px 0;
      gap: 8px;
    }
  
    .project-row {
      background-color: $secondary-color;
      cursor: pointer;
    }
  
    .project-row:hover {
      background-color: $form-color;
    }
  
    .pagination {
      display: flex;
      justify-content: center;
      margin-top: 20px;
    }
  
    .pagination button {
      margin: 0 5px;
      padding: 5px 10px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      cursor: pointer;
    }
  
    .pagination button:hover,
    .pagination button.active {
      background-color: darken($primary-color, 10%);
    }
  }
  </style>
  